var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx } from "preact/jsx-runtime";
// @ts-ignore
import { createRootFragment } from "preact-root-fragment";
import { render } from "preact";
import { useEffect, useRef } from "preact/hooks";
// @ts-ignore
import * as THREE from "three";
// ─── Global Scroll Velocity Tracker ───────────────────────────────────────
let globalScrollVelocity = 0;
let scrollTrackingInitialized = false;
function initScrollTracking() {
    if (scrollTrackingInitialized)
        return;
    scrollTrackingInitialized = true;
    let lastY = window.scrollY;
    let velocity = 0;
    let decayTimeout = null;
    const decay = () => {
        velocity *= 0.95;
        globalScrollVelocity = Math.min(Math.abs(velocity) * 0.005, 0.05);
        if (globalScrollVelocity > 0.0001) {
            decayTimeout = setTimeout(decay, 16);
        }
        else {
            globalScrollVelocity = 0;
            decayTimeout = null;
        }
    };
    const handleScroll = () => {
        const currentY = window.scrollY;
        const deltaY = currentY - lastY;
        lastY = currentY;
        velocity = deltaY;
        globalScrollVelocity = Math.min(Math.abs(velocity) * 0.005, 0.05);
        if (decayTimeout)
            clearTimeout(decayTimeout);
        decayTimeout = setTimeout(() => decay(), 100);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
}
// ─── Shaders ───────────────────────────────────────────────────────────────
const vertexShader = `
varying vec2 vUv;
void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;
const fragmentShader = `
precision highp float;

uniform sampler2D tDiffuse;
uniform vec2 uMouse;
uniform float uVelo;
uniform vec2 uvRate1;
uniform float uIntensity;
uniform float uSize;
uniform float uFalloff;
varying vec2 vUv;

float circle(vec2 uv, vec2 discCenter, float discRadius, float borderSize) {
    vec2 diff = uv - discCenter;
    float dist = length(diff);
    return smoothstep(discRadius + borderSize, discRadius - borderSize, dist);
}

void main() {
    vec2 texUV = (vUv - 0.5) * uvRate1 + 0.5;
    float c = circle(vUv, uMouse, uSize, uFalloff);
    vec2 clampedUV = clamp(texUV, 0.0, 1.0);

    float offsetR = c * uVelo * uIntensity * 0.5;
    float offsetG = c * uVelo * uIntensity * 1.5;
    float offsetB = c * uVelo * uIntensity * 3.0;

    float r = texture2D(tDiffuse, clamp(clampedUV + offsetR, 0.0, 1.0)).r;
    float g = texture2D(tDiffuse, clamp(clampedUV + offsetG, 0.0, 1.0)).g;
    float b = texture2D(tDiffuse, clamp(clampedUV + offsetB, 0.0, 1.0)).b;

    gl_FragColor = vec4(r, g, b, 1.0);
}
`;
const ChromaticImage = ({ src, alt = "", className = "", intensity = 1.5, mobileIntensity = 0.1, size = 0.3, falloff = 0.3, animationDuration = 1000, }) => {
    const containerRef = useRef(null);
    const canvasRef = useRef(null);
    useEffect(() => {
        // Initialize global scroll tracking
        initScrollTracking();
        const container = containerRef.current;
        const canvas = canvasRef.current;
        if (!container || !canvas)
            return;
        const width = container.clientWidth;
        const height = container.clientHeight;
        const renderer = new THREE.WebGLRenderer({
            canvas,
            antialias: true,
            alpha: true,
        });
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
        renderer.setSize(width, height);
        const scene = new THREE.Scene();
        const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
        // ── Device detection ───────────────────────────────────────
        const isTouch = "ontouchstart" in window || window.navigator.maxTouchPoints > 0;
        // ── Animation duration config ────────────────────────────────
        // Convert duration to per-frame decay factor (assuming 60fps = 16.67ms per frame)
        const decayFactorOutside = 1 - 16.67 / (animationDuration * 0.5);
        const decayFactorInside = 1 - 16.67 / (animationDuration * 2);
        // ── Mouse state (global, tracked via window) ──────────
        const mouse = new THREE.Vector2(-100, -100);
        const followMouse = new THREE.Vector2(-100, -100);
        const prevMouse = new THREE.Vector2(-100, -100);
        let targetSpeed = 0;
        let insideElement = false;
        let isIntersecting = false;
        // ── Texture & material ──────────────────────────────────────
        let material = null;
        let mesh = null;
        // ── Intersection Observer ────────────────────────────────────
        let observer = null;
        // Mobile uses full-image effect with different intensity
        const effectSize = isTouch ? 2.0 : size;
        const effectFalloff = isTouch ? 1.0 : falloff;
        const effectIntensity = isTouch ? mobileIntensity : intensity;
        // Load image manually with CORS support
        const loadImg = new Image();
        loadImg.crossOrigin = "anonymous";
        loadImg.onload = () => {
            const texture = new THREE.Texture(loadImg);
            texture.minFilter = THREE.LinearFilter;
            texture.magFilter = THREE.LinearFilter;
            texture.needsUpdate = true;
            const imageAspect = loadImg.naturalHeight / loadImg.naturalWidth;
            const containerAspect = height / width;
            let a1, a2;
            if (containerAspect > imageAspect) {
                a1 = (width / height) * imageAspect;
                a2 = 1;
            }
            else {
                a1 = 1;
                a2 = containerAspect / imageAspect;
            }
            material = new THREE.ShaderMaterial({
                uniforms: {
                    tDiffuse: { value: texture },
                    uMouse: { value: new THREE.Vector2(-100, -100) },
                    uVelo: { value: 0 },
                    uvRate1: { value: new THREE.Vector2(a1, a2) },
                    uIntensity: { value: effectIntensity },
                    uSize: { value: effectSize },
                    uFalloff: { value: effectFalloff },
                },
                vertexShader,
                fragmentShader,
            });
            const geometry = new THREE.PlaneGeometry(2, 2);
            mesh = new THREE.Mesh(geometry, material);
            scene.add(mesh);
        };
        loadImg.onerror = (e) => {
            console.error("[ChromaticImage] Failed to load image:", src, e);
        };
        loadImg.src = src;
        // ── Resize handler ──────────────────────────────────────────
        const handleResize = () => {
            const w = container.clientWidth;
            const h = container.clientHeight;
            if (w === 0 || h === 0)
                return;
            renderer.setSize(w, h);
        };
        window.addEventListener("resize", handleResize);
        // ── Intersection Observer ───────────────────────────────────
        observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.target === container) {
                    isIntersecting = entry.isIntersecting;
                }
            });
        }, { threshold: 0.01, rootMargin: "-100px" });
        observer.observe(container);
        // ── Mouse events (tracked globally) ─────────────────────────
        const getContainerUV = (clientX, clientY) => {
            const rect = container.getBoundingClientRect();
            const x = (clientX - rect.left) / rect.width;
            const y = 1.0 - (clientY - rect.top) / rect.height;
            return { x, y };
        };
        const handlePointerMove = (e) => {
            if (isTouch)
                return; // Skip mouse events on touch devices
            const { x, y } = getContainerUV(e.clientX, e.clientY);
            mouse.set(x, y);
            const rect = container.getBoundingClientRect();
            insideElement =
                e.clientX >= rect.left &&
                    e.clientX <= rect.right &&
                    e.clientY >= rect.top &&
                    e.clientY <= rect.bottom;
        };
        if (!isTouch) {
            window.addEventListener("mousemove", handlePointerMove, {
                passive: true,
            });
        }
        // ── Animation loop ──────────────────────────────────────────
        let animId;
        const animate = () => {
            // Only animate if element is in viewport - saves GPU on mobile
            if (!isIntersecting) {
                // Still request frame to check intersection status, but skip rendering
                animId = requestAnimationFrame(animate);
                // Decay velocity when not visible
                targetSpeed *= decayFactorOutside;
                return;
            }
            animId = requestAnimationFrame(animate);
            if (isTouch) {
                // On touch devices, use scroll velocity and center the effect
                followMouse.set(0.5, 0.5);
                targetSpeed = globalScrollVelocity * 2; // Amplify scroll velocity for better effect
            }
            else {
                // Velocity calculation for desktop
                const dx = prevMouse.x - mouse.x;
                const dy = prevMouse.y - mouse.y;
                const speed = Math.sqrt(dx * dx + dy * dy);
                targetSpeed -= 0.1 * (targetSpeed - speed);
                // Smooth mouse follow
                followMouse.x += 0.1 * (mouse.x - followMouse.x);
                followMouse.y += 0.1 * (mouse.y - followMouse.y);
                // When not inside element, decay velocity toward 0 smoothly
                if (!insideElement) {
                    targetSpeed *= decayFactorOutside;
                }
                else {
                    targetSpeed *= decayFactorInside;
                }
                // Update previous
                prevMouse.x = mouse.x;
                prevMouse.y = mouse.y;
            }
            // Update uniforms
            if (material) {
                material.uniforms.uMouse.value.copy(followMouse);
                material.uniforms.uVelo.value = Math.min(targetSpeed, 0.05);
            }
            renderer.render(scene, camera);
        };
        animate();
        // ── Cleanup ─────────────────────────────────────────────────
        return () => {
            cancelAnimationFrame(animId);
            window.removeEventListener("resize", handleResize);
            if (!isTouch) {
                window.removeEventListener("mousemove", handlePointerMove);
            }
            if (observer) {
                observer.unobserve(container);
                observer.disconnect();
            }
            if (mesh) {
                mesh.geometry.dispose();
                if (material) {
                    material.dispose();
                    const tex = material.uniforms.tDiffuse.value;
                    if (tex)
                        tex.dispose();
                }
            }
            renderer.dispose();
        };
    }, [src]);
    return (_jsx("div", { ref: containerRef, className: `chromatic-image-container ${className}`, style: {
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
        }, children: _jsx("canvas", { ref: canvasRef, style: {
                display: "block",
                width: "100%",
                height: "100%",
            } }) }));
};
// ─── Auto-init ───────────────────────────────────────────────────────────────
function autoInitChromaticImages() {
    return __awaiter(this, void 0, void 0, function* () {
        document
            .querySelectorAll(".chromatic-image-container-wrapper")
            .forEach((el) => el.remove());
        document
            .querySelectorAll("img[data-chromatic]")
            .forEach((img) => {
            img.style.display = "";
            img.style.visibility = "";
            img.style.pointerEvents = "";
        });
        const images = document.querySelectorAll("img[data-chromatic]");
        images.forEach((img, index) => {
            var _a, _b;
            if (img.dataset.chromaticInit === "1")
                return;
            img.dataset.chromaticInit = "1";
            const chromaticAttr = img.getAttribute("data-chromatic");
            const src = chromaticAttr && chromaticAttr !== "1" ? chromaticAttr : img.src;
            const alt = img.alt || "";
            const className = img.className || "";
            const rect = img.getBoundingClientRect();
            const w = rect.width || img.clientWidth || img.naturalWidth;
            const h = rect.height || img.clientHeight || img.naturalHeight;
            const parent = img.parentNode;
            parent === null || parent === void 0 ? void 0 : parent.querySelectorAll(".chromatic-image-container-wrapper").forEach((el) => el.remove());
            img.style.visibility = "";
            img.style.pointerEvents = "";
            const wrapper = document.createElement("div");
            wrapper.classList.add("chromatic-image-container-wrapper");
            wrapper.dataset.enhanced = "true";
            wrapper.style.position = "relative";
            wrapper.style.display = "inline-block";
            wrapper.style.width = `${w}px`;
            wrapper.style.height = `${h}px`;
            const target = document.createElement("div");
            target.style.width = "100%";
            target.style.height = "100%";
            wrapper.appendChild(target);
            (_a = img.parentNode) === null || _a === void 0 ? void 0 : _a.insertBefore(wrapper, img.nextSibling);
            img.style.display = "none";
            // Render
            setTimeout(() => {
                render(_jsx(ChromaticImage, { src: src, alt: alt, className: className, intensity: 0.2 }), createRootFragment(wrapper, target));
            }, 100);
            // Cleanup
            const pageIdContainer = document.querySelector("[data-p-id]");
            const pageId = (_b = pageIdContainer === null || pageIdContainer === void 0 ? void 0 : pageIdContainer.dataset) === null || _b === void 0 ? void 0 : _b.pId;
            window.addBeforeNavigate(() => {
                // Remove enhancement
                wrapper.remove();
                // Restore original visibility
                img.style.display = "";
                delete img.__enhanced;
            }, `cleanup-img-${index}-${pageId}`, false);
        });
    });
}
export default ChromaticImage;
export { autoInitChromaticImages };
